import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import { Video, VideoSlug } from '@/types/videos';



const usePartnerIdForVideos = () => {
  const { profile, user } = useAuth();
  const { isWhiteLabel } = useBranding();

  return (() => {
    if (!isWhiteLabel) return null;
    if (profile?.partner_id) return profile.partner_id;
    if (profile?.user_type === 'partner' && user?.id) return user.id;
    return null;
  })();
};

export const getVideos  = async (partnerId: string | null) => {
  let query = supabase.from('videos').select('*'); 
  if (partnerId) {
    query = query.eq('partner_id', partnerId);
  } else {
    query = query.is('partner_id', null);
  }
  const { data, error } = await query.order('created_at');
  if (error) throw error;
  return data || [];
};

export const useVideos = () => {
  const partnerId = usePartnerIdForVideos();

  return useQuery({
    queryKey: ['videos', partnerId],
    queryFn: async (): Promise<Video[]> =>  getVideos(partnerId),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useVideoBySlug = (slug: VideoSlug): string | null => {
  const { data: videos } = useVideos();
   const video = videos?.find(video => video.slug === slug) || null;
   return video?.video_url || null;
};

export const usePrefetchVideos = () => {
  const queryClient = useQueryClient();
  const partnerId = usePartnerIdForVideos();

  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: ['videos', partnerId],
      queryFn: async (): Promise<Video[]> =>  getVideos(partnerId),
      staleTime: Infinity,
    });
  }, [queryClient, partnerId]);
};
